'use client'

import { TrackingCodeInput } from '@systemeio/components'
import type { FocusEvent, ReactNode } from 'react'
import { SafeHtmlText } from './safe-html-text'

interface CodeEditorProps {
  disabled?: boolean
  error?: string
  label?: ReactNode
  onBlur?: (value: string) => void
  onChange: (value: string) => void
  setIsValid?: (value: boolean) => void
  statusIcon?: ReactNode
  value?: string | null
}

function CodeEditor({
  disabled,
  error,
  label,
  onBlur,
  onChange,
  setIsValid,
  statusIcon,
  value,
}: CodeEditorProps) {
  const currentValue = value ?? ''

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    // The diagnostic gutter renders focusable tooltip triggers inside the control,
    // so ignore focus moves that stay within the editor.
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) {
      return
    }
    onBlur?.(currentValue)
  }

  return (
    <TrackingCodeInput.Root
      // Server-side errors force invalid chrome; otherwise it derives from diagnostics.
      invalid={error ? true : undefined}
      onValidationChange={state => setIsValid?.(state.valid)}
      onValueChange={onChange}
      readOnly={disabled}
      value={currentValue}
    >
      {label ? <TrackingCodeInput.Label>{label}</TrackingCodeInput.Label> : null}

      <div className={statusIcon ? 'relative' : undefined}>
        <TrackingCodeInput.Control onBlur={onBlur && !disabled ? handleBlur : undefined} />
        {statusIcon && (
          <div className="bg-bg-primary shadow-1 absolute top-2 right-2 z-30 rounded-lg p-2">
            {statusIcon}
          </div>
        )}
      </div>

      <TrackingCodeInput.Error>
        {error ? <SafeHtmlText html={error} /> : undefined}
      </TrackingCodeInput.Error>
    </TrackingCodeInput.Root>
  )
}

export { CodeEditor }
export type { CodeEditorProps }
